import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Joyride, STATUS, ACTIONS, EVENTS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  setTourRun, 
  setTourEnabled, 
  startTour, 
  stopTour, 
  completeTour, 
  setTourStepIndex, 
  setTourData,
  resetTourState 
} from "../../redux/MainReducer";
import { getWithQueryApiServices, postWithQueryApiServices } from "../../apis/api";
import { apiRoutes } from "../../apis/apiPath";
import { tourSteps } from "./TourSteps";
import Cookies from "universal-cookie";
import "./tour.css";

const getActiveSteps = (tourKey, stepsConfig) => {
  return stepsConfig[tourKey] ? [...stepsConfig[tourKey]] : [];
};

const getTourTargetSelector = (key) => {
  if (key === "companies") return "#tour-company-grid";
  if (key === "categories") return "#tour-category-grid";
  if (key === "security") return "#tour-securityprofile-grid";
  if (key === "users") return "#tour-user-grid";
  if (key === "invoices") return "#tour-invoice-grid";
  if (key === "reporting") return "#tour-reporting-type-panel";
  if (key === "advancedSearch") return "#tour-advanced-search-input";
  if (key === "home") return "body";
  return "body";
};

const GRID_SELECTORS = [
  "#tour-category-grid",
  "#tour-company-grid",
  "#tour-securityprofile-grid",
  "#tour-user-grid",
  "#tour-invoice-grid"
];

const GRID_TOUR_MAPPINGS_FOR_SELECT = {
  categories: "#tour-category-grid",
  companies: "#tour-company-grid",
  security: "#tour-securityprofile-grid",
  users: "#tour-user-grid",
  invoices: "#tour-invoice-grid"
};

const GRID_TOUR_TO_LABEL_MAPPINGS = {
  categories: "category",
  companies: "company",
  security: "securityprofile",
  users: "user",
  invoices: "invoice"
};

  const lockAllScrollPositions = () => {
  const allScrollables = document.querySelectorAll('*');
  const locked = [];
  allScrollables.forEach(el => {
    if (el.scrollTop > 0 || el.scrollLeft > 0) {
      const top = el.scrollTop;
      const left = el.scrollLeft;
      locked.push({ el, top, left });
    }
  });
  return locked;
};

const restoreScrollPositions = (locked) => {
  locked.forEach(({ el, top, left }) => {
    el.scrollTop = top;
    el.scrollLeft = left;
  });
};

const selectFirstRowOfGrid = (gridSelector, tourKey, hasAutoSelectedRef, lockScroll = false) => {
  const gridLabel = GRID_TOUR_TO_LABEL_MAPPINGS[tourKey];
  
  if (gridLabel && window.reactTourHelpers && window.reactTourHelpers[gridLabel]) {
    
    // ✅ Lock scroll before selection
    const locked = lockScroll ? lockAllScrollPositions() : [];
    
    const success = window.reactTourHelpers[gridLabel].selectFirstRow();
    
    if (success) {
      if (hasAutoSelectedRef && hasAutoSelectedRef.current) {
        hasAutoSelectedRef.current[tourKey] = true;
      }
      // ✅ Restore scroll after selection in next few frames
      if (lockScroll && locked.length > 0) {
        requestAnimationFrame(() => {
          restoreScrollPositions(locked);
          requestAnimationFrame(() => {
            restoreScrollPositions(locked);
          });
        });
      }
      return true;
    }
  }
  return false;
};

const TOUR_SEQUENCE = [
  { key: "home", path: "/home" },
  { key: "advancedSearch", path: "/home" },
  { key: "categories", path: "/category/list" },
  { key: "companies", path: "/company/list" },
  { key: "security", path: "/security/list" },
  { key: "users", path: "/users" },
  { key: "reporting", path: "/reporting" },
  { key: "invoices", path: "/invoice/list" },
];

const getNextTour = (currentKey) => {
  const currentIndex = TOUR_SEQUENCE.findIndex(item => item.key === currentKey);
  if (currentIndex !== -1 && currentIndex < TOUR_SEQUENCE.length - 1) {
    return TOUR_SEQUENCE[currentIndex + 1];
  }
  return null;
};

const TourGuide = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux Tour State
  const tourRun = useSelector((state) => state.mainReducer?.tourRun);
  const isTourEnabled = useSelector((state) => state.mainReducer?.isTourEnabled);
  const completedTours = useSelector((state) => state.mainReducer?.completedTours) || {};
  const activeTour = useSelector((state) => state.mainReducer?.activeTour);
  const tourStatusLoaded = useSelector((state) => state.mainReducer?.tourStatusLoaded);
  
  // Auth & Tenant Context from Redux/Cookies fallback
  const cookies = new Cookies();
  const token = useSelector((state) => state.mainReducer?.token) || cookies.get("token");
  const tenantID = useSelector((state) => state.mainReducer?.tenantID || state.mainReducer?.tenantId) || cookies.get("defaulttenant") || cookies.get("tenantId");
  const currentCompany = useSelector((state) => state.mainReducer?.defaultcompany) || cookies.get("defaultcompany");

  const isAuthenticated = !!token;

  const [currentSteps, setCurrentSteps] = useState([]);
  const [currentTourKey, setCurrentTourKey] = useState(null);
  const [sessionCompletedTours, setSessionCompletedTours] = useState({});
  const [hasActiveSecurityProfile, setHasActiveSecurityProfile] = useState(false);
  const [isInvoiceFormActive, setIsInvoiceFormActive] = useState(false);
  const [reportingStepsHash, setReportingStepsHash] = useState("");
  const activeTargetRef = useRef(null);
  const lastSelectTimeRef = useRef(0);
  const hasActiveSecurityProfileRef = useRef(false);
  const isInvoiceFormActiveRef = useRef(false);
  const reportingStepsHashRef = useRef("");
  const hasAutoSelectedRef = useRef({});
  const tourRunRef = useRef(tourRun);
  const tourDismissedRef = useRef(false);
  const gridStepReachedRef = useRef(false);
  const currentTourKeyRef = useRef(null);
  const filteredStepsRef = useRef([]);

  // Helper to determine the tour key for the current pathname
  const getTourKeyForPath = useCallback((path) => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("autoSubmit") === "true" || window.skipTourForSearch) {
      return null;
    }
    const forcedTourKey = searchParams.get("startTour");
    if (forcedTourKey && tourSteps[forcedTourKey]) {
      return forcedTourKey;
    }

    // Prioritize advancedSearch if the advanced search dialog is currently open in the DOM
    if (document.querySelector("#tour-advanced-search-input")) {
      return "advancedSearch";
    }

    if (path.includes("/category/list")) return "categories";
    if (path.includes("/company/list")) return "companies";
    if (path.includes("/invoice/list")) return "invoices";
    if (path.includes("/reporting")) return "reporting";
    if (path.includes("/security/list")) return "security";
    if (path.includes("/users") || path.endsWith("users")) return "users";
    if (path.includes("/subscription/list")) return "subscription";
    if (path.includes("/home") || path === "/") {
      if ((completedTours["home"] || sessionCompletedTours["home"]) && !completedTours["advancedSearch"] && !sessionCompletedTours["advancedSearch"]) {
        return "advancedSearch";
      }
      return "home";
    }
    return null;
  }, [location.search, completedTours, sessionCompletedTours]);


  const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currentPathTourKey = getTourKeyForPath(location.pathname);

  const filteredSteps = useMemo(() => {
    let steps = currentSteps;
    if (currentTourKey === "security") {
      if (!hasActiveSecurityProfile) {
        steps = currentSteps.slice(0, 4);
      }
    } else if (currentTourKey === "reporting") {
      // Show all steps without filtering since they are all rendered during the tour
      steps = currentSteps;
    } else if (currentTourKey === "invoices") {
      if (!isInvoiceFormActive) {
        steps = currentSteps.slice(0, 3);
      }
    }
    return steps.map(step => {
      let placement = step.placement;
      if (isMobileOrTablet && step.target !== "#tour-advanced-search-btn" && (placement === "left" || placement === "right")) {
        placement = "bottom";
      }
      let target = step.target;
      if (isMobileOrTablet && typeof target === "string") {
        if (target.startsWith("#tour-nav-")) {
          target = `${target}-mobile`;
        } else if (target === "#tour-category-grid") {
          target = "#tour-category-grid .MuiDataGrid-row:first-of-type";
        } else if (target === "#tour-company-grid") {
          target = "#tour-company-grid .MuiDataGrid-row:first-of-type";
        } else if (target === "#tour-securityprofile-grid") {
          target = "#tour-securityprofile-grid .MuiDataGrid-row:first-of-type";
        }
      }
      return { ...step, target, placement, disableBeacon: true };
    });
  }, [currentSteps, currentTourKey, hasActiveSecurityProfile, isInvoiceFormActive, reportingStepsHash, isMobileOrTablet]);

  useEffect(() => {
    tourRunRef.current = tourRun;
  }, [tourRun]);

  useEffect(() => {
    currentTourKeyRef.current = currentTourKey;
  }, [currentTourKey]);

  useEffect(() => {
    filteredStepsRef.current = filteredSteps;
  }, [filteredSteps]);

  // Block ESC key globally when tour is active to prevent overlay removal
  useEffect(() => {
    if (!tourRun) return;

    const blockEsc = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener("keydown", blockEsc, true);
    return () => document.removeEventListener("keydown", blockEsc, true);
  }, [tourRun]);

  useEffect(() => {
    if (!tourRun || !currentTourKey) return;

    const gridLabel = GRID_TOUR_TO_LABEL_MAPPINGS[currentTourKey];
    if (!gridLabel) return;
    if (hasAutoSelectedRef.current[currentTourKey]) return;

    const autoSelectTarget = GRID_TOUR_MAPPINGS_FOR_SELECT[currentTourKey];
    if (!autoSelectTarget) return;

    // Poll until rows are available and helper can select
    const intervalId = setInterval(() => {
      if (hasAutoSelectedRef.current[currentTourKey]) {
        clearInterval(intervalId);
        return;
      }

      // ✅ Save scroll positions before selection
      const scrollableEls = document.querySelectorAll('[style*="overflow"], .MuiDataGrid-virtualScroller');
      const scrollPositions = Array.from(scrollableEls).map(el => ({
        el,
        top: el.scrollTop,
        left: el.scrollLeft
      }));

      const success = selectFirstRowOfGrid(autoSelectTarget, currentTourKey, hasAutoSelectedRef, true);

      // ✅ Restore scroll positions after selection
      if (success) {
        requestAnimationFrame(() => {
          scrollPositions.forEach(({ el, top, left }) => {
            el.scrollTop = top;
            el.scrollLeft = left;
          });
        });
        clearInterval(intervalId);
      }
    }, 300);

    return () => clearInterval(intervalId);
  }, [tourRun, currentTourKey]);

  useEffect(() => {
    if (currentTourKey) {
      hasActiveSecurityProfileRef.current = false;
      isInvoiceFormActiveRef.current = false;
      reportingStepsHashRef.current = "";
      hasAutoSelectedRef.current[currentTourKey] = false;
      gridStepReachedRef.current = false;
    }
  }, [currentTourKey]);

  useEffect(() => {
    const checkActive = () => {
      if (!currentTourKey) return;

      // 1. Security check
      if (currentTourKey === "security") {
        const active = !!document.querySelector("#tour-add-iprule-btn");
        if (hasActiveSecurityProfileRef.current !== active) {
          hasActiveSecurityProfileRef.current = active;
          setHasActiveSecurityProfile(active);
        }
      }

      // 2. Invoice check
      if (currentTourKey === "invoices") {
        const formPane = document.querySelector("#tour-invoice-form-pane");
        const active = !!(formPane && formPane.style.cursor === "auto");
        if (isInvoiceFormActiveRef.current !== active) {
          isInvoiceFormActiveRef.current = active;
          setIsInvoiceFormActive(active);
        }
      }

      // 3. Reporting check
      if (currentTourKey === "reporting") {
        const stepsConfig = tourSteps.reporting || [];
        const presentTargets = stepsConfig
          .map(step => {
            if (step.target === "body" || step.target === "#tour-reporting-startover-btn") return "1";
            return document.querySelector(step.target) ? "1" : "0";
          })
          .join("");
        if (reportingStepsHashRef.current !== presentTargets) {
          reportingStepsHashRef.current = presentTargets;
          setReportingStepsHash(presentTargets);
        }
      }

      // 4. Automatically select the first row for any grid on the active tour page
      // Only auto-select when the current tour step is targeting the grid
      const gridTourMappings = {
        categories: "#tour-category-grid",
        companies: "#tour-company-grid",
        security: "#tour-securityprofile-grid",
        users: "#tour-user-grid",
        invoices: "#tour-invoice-grid"
      };

      const activeGridSelector = gridTourMappings[currentTourKey];
      if (tourRunRef.current && activeGridSelector && !hasAutoSelectedRef.current[currentTourKey] && gridStepReachedRef.current) {
        selectFirstRowOfGrid(activeGridSelector, currentTourKey, hasAutoSelectedRef, true);
      }
    };

    checkActive();

    const observer = new MutationObserver(checkActive);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true
    });

    const handleDocumentClick = () => {
      checkActive();
    };
    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      observer.disconnect();
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [currentTourKey]);

  const searchParams = new URLSearchParams(location.search);
  const startTourParam = searchParams.get("startTour");
  const isFromChatbot = searchParams.get("fromChatbot") === "true";
  const forcedTourKey = startTourParam || (isFromChatbot ? currentPathTourKey : null);
  const isForced = !!(forcedTourKey && forcedTourKey === currentPathTourKey);

  const clearStartTourParam = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("startTour") || searchParams.has("fromChatbot")) {
      searchParams.delete("startTour");
      searchParams.delete("fromChatbot");
      const newSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true },
      );
    }
  };

  // Fetch tour status from backend once user is authenticated
  const fetchTourStatus = async () => {
    try {
      const queryParam = tenantID ? `defaulttenant=${tenantID}` : "";
      const response = await getWithQueryApiServices(
        apiRoutes.GET_TOURS,
        queryParam
      );
      if (response?.data?.data) {
        dispatch(setTourData({
          completedTours: response.data.data.completedTours || {},
          isTourEnabled: response.data.data.isTourEnabled
        }));
      } else {
        dispatch(setTourData({ completedTours: {}, isTourEnabled: true }));
      }
    } catch (err) {
      console.error("[TourGuide] fetchTourStatus error:", err);
      dispatch(setTourData({ completedTours: {}, isTourEnabled: true }));
    }
  };

  const completeTourStatus = async (tourKey) => {
    try {
      const queryParam = tenantID ? `defaulttenant=${tenantID}` : "";
      await postWithQueryApiServices(
        apiRoutes.COMPLETE_TOUR,
        queryParam,
        { tourKey, completed: true }
      );
      dispatch(completeTour(tourKey));

      // Check if all defined tours are now completed
      const nextCompletedTours = { ...completedTours, [tourKey]: true };
      const allKeys = Object.keys(tourSteps);
      const allCompleted = allKeys.every(key => nextCompletedTours[key]);

      if (allCompleted) {
        await postWithQueryApiServices(
          apiRoutes.COMPLETE_TOUR,
          queryParam,
          { tourKey: "all", completed: true }
        );
        dispatch(setTourData({ completedTours: {}, isTourEnabled: false }));
      }
    } catch (err) {
      console.error("[TourGuide] completeTourStatus error:", err);
    }
  };

  const skipWholeAppTour = async () => {
    await completeTourStatus("all");
    dispatch(setTourEnabled(false));
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchTourStatus();
    }
  }, [isAuthenticated, tenantID]);

  // Clear sessionCompletedTours if the tour is re-enabled/reset (all tours uncompleted)
  useEffect(() => {
    if (isTourEnabled && Object.keys(completedTours).length === 0) {
      setSessionCompletedTours({});
    }
  }, [isTourEnabled, completedTours]);

  // Prevent any page or grid scrolling during the active tour (disabled on mobile/tablet to allow viewport scrolling to targets)
  useEffect(() => {
    if (tourRun && !isMobileOrTablet) {
      const handleScroll = (e) => {
        if (
          e.target === window ||
          e.target === document ||
          e.target === document.documentElement ||
          e.target === document.body
        ) {
          window.scrollTo(0, 0);
        } else if (
          e.target.classList &&
          e.target.classList.contains("MuiDataGrid-virtualScroller")
        ) {
          e.target.scrollTop = 0;
          e.target.scrollLeft = 0;
        }
      };

      window.addEventListener("scroll", handleScroll, true);
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [tourRun, isMobileOrTablet]);

  // Reset sessionCompletedTours closed status for the new path so it can run once per page visit
  useEffect(() => {
    const tourKey = getTourKeyForPath(location.pathname);

    if (tourKey) {
      // Reset dismissed flag when navigating to a new page so tours can run there
      tourDismissedRef.current = false;
      setSessionCompletedTours(prev => {
        if (prev[tourKey]) {
          const copy = { ...prev };
          delete copy[tourKey];
          return copy;
        }
        return prev;
      });
    }
  }, [location.pathname, getTourKeyForPath]);

  // Set window.skipTourForSearch when autoSubmit=true is present in query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("autoSubmit") === "true") {
      window.skipTourForSearch = true;
    }
  }, [location.search]);

  // Reset window.skipTourForSearch when navigating away from home page
  useEffect(() => {
    if (location.pathname !== "/home" && location.pathname !== "/") {
      window.skipTourForSearch = false;
    }
  }, [location.pathname]);

  // Watch for the Advanced Search dialog opening/closing to dynamically switch the tour key
  useEffect(() => {
    const isGlobalSuperAdmin = !!cookies.get("defaulttenant") || String(cookies.get("isGlobalSuperAdmin")) === "true";
    if (!isTourEnabled || !isAuthenticated || isGlobalSuperAdmin || !tourStatusLoaded) {
      return;
    }

    const checkDialog = () => {
      const isDialogOpen = !!document.querySelector("#tour-advanced-search-input");
      if (isDialogOpen) {
        if (!completedTours["advancedSearch"] && !sessionCompletedTours["advancedSearch"]) {
          if (currentTourKeyRef.current !== "advancedSearch") {
            dispatch(stopTour());
            setCurrentTourKey("advancedSearch");
            const steps = getActiveSteps("advancedSearch", tourSteps);
            setCurrentSteps(steps);
            dispatch(startTour("advancedSearch"));
          }
        }
      } else {
        // If the dialog is closed and the current tour key is advancedSearch, stop and reset
        if (currentTourKeyRef.current === "advancedSearch") {
          dispatch(stopTour());
          const nextTourKey = getTourKeyForPath(location.pathname);
          setCurrentTourKey(nextTourKey);
          if (nextTourKey) {
            const steps = getActiveSteps(nextTourKey, tourSteps);
            setCurrentSteps(steps);
          }
        }
      }
    };

    // Run check initially
    checkDialog();

    const observer = new MutationObserver(checkDialog);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [
    isTourEnabled,
    isAuthenticated,
    tourStatusLoaded,
    completedTours,
    sessionCompletedTours,
    location.pathname,
    getTourKeyForPath,
    dispatch
  ]);

  // Detect route change and trigger the tour
  useEffect(() => {
    const tourKey = getTourKeyForPath(location.pathname);
    const isGlobalSuperAdmin = !!cookies.get("defaulttenant") || String(cookies.get("isGlobalSuperAdmin")) === "true";

    if (!isForced && (!isTourEnabled || !isAuthenticated || isGlobalSuperAdmin)) {
      dispatch(stopTour());
      return;
    }

    // Wait for backend tour status to load before deciding to show tours
    if (!isForced && !tourStatusLoaded) {
      return;
    }

    if (tourKey && tourSteps[tourKey]) {
      // If the tour is already running for the current key and we are not forcing a restart, do nothing
      if (tourRunRef.current && currentTourKey === tourKey && !forcedTourKey) {
        return;
      }

      // Check if this tour has already been completed in backend (unless forced)
      if (completedTours[tourKey] && !isForced) {
        dispatch(stopTour());
        return;
      }

      // Check if this tour has already been closed in the current page visit
      if (sessionCompletedTours[tourKey] && !isForced) {
        dispatch(stopTour());
        return;
      }

      // If the tour was just dismissed via ESC, mark it as session-completed and don't restart
      if (tourDismissedRef.current) {
        tourDismissedRef.current = false;
        dispatch(stopTour());
        return;
      }

      // Stop any currently running tour immediately
      dispatch(stopTour());
      setCurrentTourKey(tourKey);

      const initialSteps = getActiveSteps(tourKey, tourSteps);
      setCurrentSteps(initialSteps);

      // Start the tour on page load/render with a short delay to ensure DOM readiness
      const timer = setTimeout(() => {
        // If the tour was already started or completed in the meantime, do not override it
        if (tourRunRef.current && (document.querySelector(".react-joyride__overlay") || document.querySelector(".custom-tour-tooltip"))) {
          return;
        }

        const isPageLoaded = document.querySelector(
          getTourTargetSelector(tourKey)
        );

        if (!isPageLoaded) {
          return;
        }

        const finalSteps = getActiveSteps(tourKey, tourSteps);
        setCurrentSteps(finalSteps);
        dispatch(startTour(tourKey));
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      dispatch(stopTour());
    }
  }, [
    location.pathname,
    location.search,
    isTourEnabled,
    isAuthenticated,
    tourStatusLoaded,
    sessionCompletedTours,
    completedTours,
    currentTourKey,
    getTourKeyForPath,
    dispatch
  ]);

  // Observer to check when page content renders and auto-trigger tour
  useEffect(() => {
    const targetTourKey = getTourKeyForPath(location.pathname);
    if (currentTourKey !== targetTourKey) {
      return;
    }

    const isGlobalSuperAdmin = !!cookies.get("defaulttenant") || String(cookies.get("isGlobalSuperAdmin")) === "true";

    const shouldSkip =
      !isForced &&
      (!isTourEnabled ||
        !isAuthenticated ||
        isGlobalSuperAdmin ||
        !tourStatusLoaded ||
        !currentTourKey ||
        completedTours[currentTourKey] ||
        sessionCompletedTours[currentTourKey]);
    if (shouldSkip) return;

    if (!tourRun) {
      // Don't restart a tour that was just dismissed
      if (tourDismissedRef.current) {
        return;
      }

      const isPageLoaded = document.querySelector(
        getTourTargetSelector(currentTourKey)
      );

      if (isPageLoaded) {
        const finalSteps = getActiveSteps(currentTourKey, tourSteps);
        setCurrentSteps(finalSteps);
        dispatch(startTour(currentTourKey));
        return;
      }

      const observer = new MutationObserver(() => {
        if (tourDismissedRef.current || tourRun) {
          observer.disconnect();
          return;
        }

        const isPageLoadedObs = document.querySelector(
          getTourTargetSelector(currentTourKey)
        );

        if (isPageLoadedObs) {
          const finalSteps = getActiveSteps(currentTourKey, tourSteps);
          setCurrentSteps(finalSteps);
          dispatch(startTour(currentTourKey));
          observer.disconnect();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      return () => observer.disconnect();
    }
  }, [
    currentTourKey,
    tourRun,
    isTourEnabled,
    isAuthenticated,
    tourStatusLoaded,
    sessionCompletedTours,
    completedTours,
    location.pathname,
    location.search,
    getTourKeyForPath,
    dispatch
  ]);

  const handleJoyrideCallback = (data) => {
    const { status, type, step, index, action } = data;

    // Smoothly scroll target into view, especially on mobile/tablet screens when navigating steps
    if ((type === EVENTS.STEP_BEFORE || type === EVENTS.TOOLTIP) && step?.target && step.target !== "body") {
      const targetEl = document.querySelector(step.target);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    // Reset grid scroller to top to prevent Joyride scroll bugs
    const resetScroll = () => {
      const activeGrid = document.querySelector("#tour-user-grid") || document.querySelector("#tour-invoice-items-grid");
      if (activeGrid) {
        const scroller = activeGrid.querySelector(".MuiDataGrid-virtualScroller");
        if (scroller) {
          scroller.scrollTop = 0;
        }
      }
    };
    resetScroll();
    requestAnimationFrame(resetScroll);

    // Track active target in ref
    activeTargetRef.current = step?.target || null;
    
    // Sync step index with Redux store
    dispatch(setTourStepIndex(index));
    
    // Check tooltip/active step of a grid step
    if (type === EVENTS.TOOLTIP && step && GRID_SELECTORS.includes(step.target)) {
      const autoTarget = GRID_TOUR_MAPPINGS_FOR_SELECT[currentTourKeyRef.current];
      if (autoTarget && !hasAutoSelectedRef.current[currentTourKeyRef.current]) {
        gridStepReachedRef.current = true;
        selectFirstRowOfGrid(autoTarget, currentTourKeyRef.current, hasAutoSelectedRef, true);
      }
    }

    // Determine the actual current/upcoming step target
    // When action is 'next' and type is 'step:after', `step` is the OLD step.
    // The upcoming step is at index + 1 (or index - 1 if action is 'prev').
    let effectiveTarget = step?.target;
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT && filteredStepsRef.current[index + 1]) {
        effectiveTarget = filteredStepsRef.current[index + 1].target;
      } else if (action === ACTIONS.PREV && filteredStepsRef.current[index - 1]) {
        effectiveTarget = filteredStepsRef.current[index - 1].target;
      }
    }

    // Auto open/close mobile drawer on mobile screens based on target step
    if (isMobileOrTablet && window.reactTourHelpers) {
      const isDrawerStep = (target) => {
        return typeof target === "string" && target.endsWith("-mobile");
      };

      let nextTarget = effectiveTarget;
      if (type === EVENTS.STEP_BEFORE && step?.target) {
        nextTarget = step.target;
      }

      if (isDrawerStep(nextTarget)) {
        if (typeof window.reactTourHelpers.openMobileDrawer === "function") {
          window.reactTourHelpers.openMobileDrawer();
        }
      } else {
        if (typeof window.reactTourHelpers.closeMobileDrawer === "function") {
          window.reactTourHelpers.closeMobileDrawer();
        }
      }
    }

    // Mark when tour reaches a grid step so MutationObserver/polling can auto-select
    if (effectiveTarget && GRID_SELECTORS.includes(effectiveTarget)) {
      gridStepReachedRef.current = true;
    }
    // Also check the current step target directly
    if (step && GRID_SELECTORS.includes(step.target)) {
      gridStepReachedRef.current = true;
    }

    const autoSelectTarget = GRID_TOUR_MAPPINGS_FOR_SELECT[currentTourKeyRef.current];

    // Fire auto-select when current step IS the grid step or next step IS the grid step
    const isCurrentStepGrid = step && GRID_SELECTORS.includes(step.target);
    const isNextStepGrid = GRID_SELECTORS.includes(effectiveTarget);
    const isGridStep = isCurrentStepGrid || isNextStepGrid;

    if (tourRunRef.current && autoSelectTarget && isGridStep && !hasAutoSelectedRef.current[currentTourKeyRef.current]) {
      const tourKey = currentTourKeyRef.current;
      let attempts = 0;
      const maxAttempts = 30;

      // ✅ Try immediately first
      const immediateSuccess = selectFirstRowOfGrid(autoSelectTarget, tourKey, hasAutoSelectedRef, true);
      
      if (!immediateSuccess) {
        const intervalId = setInterval(() => {
          attempts++;
          if (hasAutoSelectedRef.current[tourKey]) { clearInterval(intervalId); return; }
          if (attempts >= maxAttempts) { clearInterval(intervalId); return; }
          const success = selectFirstRowOfGrid(autoSelectTarget, tourKey, hasAutoSelectedRef, true);
          if (success) clearInterval(intervalId);
        }, 300);
      }
    }

    if (status === STATUS.FINISHED) {
      if (isMobileOrTablet && window.reactTourHelpers && typeof window.reactTourHelpers.closeMobileDrawer === "function") {
        window.reactTourHelpers.closeMobileDrawer();
      }
      activeTargetRef.current = null;
      if (currentTourKeyRef.current) {
        if (currentTourKeyRef.current === "advancedSearch") {
          const closeBtn = document.querySelector("#tour-advanced-search-close-btn");
          if (closeBtn) {
            closeBtn.click();
          }
        }
        completeTourStatus(currentTourKeyRef.current);
        setSessionCompletedTours(prev => ({ ...prev, [currentTourKeyRef.current]: true }));
      } else {
        dispatch(stopTour());
      }
      clearStartTourParam();
    } else if (status === STATUS.SKIPPED) {
      if (isMobileOrTablet && window.reactTourHelpers && typeof window.reactTourHelpers.closeMobileDrawer === "function") {
        window.reactTourHelpers.closeMobileDrawer();
      }
      activeTargetRef.current = null;
      if (currentTourKeyRef.current) {
        if (currentTourKeyRef.current === "advancedSearch") {
          const closeBtn = document.querySelector("#tour-advanced-search-close-btn");
          if (closeBtn) {
            closeBtn.click();
          }
        }
        completeTourStatus(currentTourKeyRef.current);
        setSessionCompletedTours(prev => ({ ...prev, [currentTourKeyRef.current]: true }));
      } else {
        dispatch(stopTour());
      }
      clearStartTourParam();
    }
  };

  const CustomTooltip = ({
    index,
    step,
    backProps,
    primaryProps,
    isLastStep,
    size,
    tooltipProps,
  }) => {
    const totalSteps = size || filteredSteps.length;

    return (
      <div
        className="custom-tour-tooltip"
        {...tooltipProps}
        onClick={(e) => {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        <button
          className="tour-close-btn"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            if (currentTourKey) {
              if (currentTourKey === "advancedSearch") {
                const closeBtn = document.querySelector("#tour-advanced-search-close-btn");
                if (closeBtn) {
                  closeBtn.click();
                }
              }
              completeTourStatus(currentTourKey);
              setSessionCompletedTours(prev => ({ ...prev, [currentTourKey]: true }));
            }
            dispatch(stopTour());
            clearStartTourParam();
          }}
          aria-label="Close Tour"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div
          className="tour-content"
          style={{
            fontSize: "1rem",
            lineHeight: "1.5",
            color: "#475569",
            marginTop: "8px",
            marginBottom: "24px",
            textAlign: "left",
            paddingRight: "36px",
          }}
        >
          {step.content || "No instruction content provided."}
        </div>

        <div
          className="tour-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {!isLastStep && !isForced ? (
            <button
              className="tour-skip-link"
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                if (currentTourKey === "advancedSearch") {
                  const closeBtn = document.querySelector("#tour-advanced-search-close-btn");
                  if (closeBtn) {
                    closeBtn.click();
                  }
                }
                skipWholeAppTour();
                clearStartTourParam();
              }}
            >
              Skip Tour
            </button>
          ) : (
            <div />
          )}

          <div
            className="tour-nav-buttons"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {index > 0 && (
              <button
                className="tour-back-btn"
                {...backProps}
                style={{
                  ...backProps.style,
                  color: "#64748b",
                  background: "none",
                  border: "none",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  outline: "none",
                  whiteSpace: "nowrap",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  const currentTarget = step?.target;
                  const isCurrentDrawer = typeof currentTarget === "string" && currentTarget.endsWith("-mobile");
                  const prevStep = filteredSteps[index - 1];
                  const prevTarget = prevStep?.target;
                  const isPrevDrawer = typeof prevTarget === "string" && prevTarget.endsWith("-mobile");

                  if (isMobileOrTablet && window.reactTourHelpers) {
                    if (isPrevDrawer && window.reactTourHelpers.openMobileDrawer) {
                      window.reactTourHelpers.openMobileDrawer();
                      setTimeout(() => {
                        if (backProps && typeof backProps.onClick === "function") {
                          backProps.onClick(e);
                        }
                      }, 150);
                    } else if (!isPrevDrawer && isCurrentDrawer && window.reactTourHelpers.closeMobileDrawer) {
                      window.reactTourHelpers.closeMobileDrawer();
                      setTimeout(() => {
                        if (backProps && typeof backProps.onClick === "function") {
                          backProps.onClick(e);
                        }
                      }, 250);
                    } else {
                      if (backProps && typeof backProps.onClick === "function") {
                        backProps.onClick(e);
                      }
                    }
                  } else {
                    if (backProps && typeof backProps.onClick === "function") {
                      backProps.onClick(e);
                    }
                  }
                }}
              >
                Back
              </button>
            )}

            {isLastStep && !isForced && getNextTour(currentTourKey) && (
              <button
                className="tour-next-tour-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  const next = getNextTour(currentTourKey);
                  if (next) {
                    if (currentTourKey) {
                      completeTourStatus(currentTourKey);
                      setSessionCompletedTours(prev => ({ ...prev, [currentTourKey]: true }));
                    }
                    dispatch(stopTour());
                    if (currentTourKey === "home") {
                      setTimeout(() => {
                        const btn = document.querySelector("#tour-advanced-search-btn");
                        if (btn) {
                          btn.click();
                        }
                      }, 100);
                    } else if (currentTourKey === "advancedSearch") {
                      const closeBtn = document.querySelector("#tour-advanced-search-close-btn");
                      if (closeBtn) {
                        closeBtn.click();
                      }
                    }
                    navigate(next.path);
                  }
                }}
              >
                Next Tour
              </button>
            )}

            <button
              className="tour-primary-btn"
              {...primaryProps}
              style={{
                ...primaryProps.style,
                backgroundColor: "#7285b3",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.95rem",
                fontWeight: "700",
                padding: "10px 18px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(114, 133, 179, 0.35)",
                outline: "none",
                whiteSpace: "nowrap",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                if (isLastStep) {
                  if (currentTourKey) {
                    completeTourStatus(currentTourKey);
                    setSessionCompletedTours(prev => ({ ...prev, [currentTourKey]: true }));
                  }
                  dispatch(stopTour());
                  clearStartTourParam();
                } else if (primaryProps && typeof primaryProps.onClick === "function") {
                  const currentTarget = step?.target;
                  const isCurrentDrawer = typeof currentTarget === "string" && currentTarget.endsWith("-mobile");
                  const nextStep = filteredSteps[index + 1];
                  const nextTarget = nextStep?.target;
                  const isNextDrawer = typeof nextTarget === "string" && nextTarget.endsWith("-mobile");

                  if (isMobileOrTablet && window.reactTourHelpers) {
                    if (isNextDrawer && window.reactTourHelpers.openMobileDrawer) {
                      window.reactTourHelpers.openMobileDrawer();
                      setTimeout(() => {
                        primaryProps.onClick(e);
                      }, 150);
                    } else if (!isNextDrawer && isCurrentDrawer && window.reactTourHelpers.closeMobileDrawer) {
                      window.reactTourHelpers.closeMobileDrawer();
                      setTimeout(() => {
                        primaryProps.onClick(e);
                      }, 250);
                    } else {
                      primaryProps.onClick(e);
                    }
                  } else {
                    primaryProps.onClick(e);
                  }
                }
              }}
            >
              {isLastStep
                ? (isMobileOrTablet ? "Finish" : "Finish Tour")
                : `Next (Step ${index + 1} of ${totalSteps})`}
            </button>
          </div>
        </div>
      </div>
    );
  };



  const isGlobalSuperAdmin = !!cookies.get("defaulttenant") || String(cookies.get("isGlobalSuperAdmin")) === "true";

  const isTourActive =
    tourRun &&
    currentTourKey === currentPathTourKey &&
    filteredSteps.length > 0 &&
    (isTourEnabled || isForced) &&
    isAuthenticated &&
    (!isGlobalSuperAdmin || isForced);

  return (
    <>
      {isTourActive && (
        <Joyride
          key={currentTourKey}
          steps={filteredSteps}
          run={tourRun}
          callback={handleJoyrideCallback}
          continuous={true}
          showProgress={true}
          showSkipButton={!isForced}
          disableOverlayClose={true}
          disableCloseOnEsc={true}
          spotlightClicks={true}
          disableBeacon={true}
          // disableScrolling={true}
// disableScrollParentFix={true}
// scrollToFirstStep={false}
          tooltipComponent={CustomTooltip}
          floaterProps={{
            disableAnimation: false,
            styles: {
              floater: {
                zIndex: 9999999,
              },
            },
          }}
          styles={{
            options: {
              zIndex: 9999999,
            },
            tooltip: {
              backgroundColor: "transparent",
              color: "inherit",
              padding: 0,
              borderRadius: 0,
              boxShadow: "none",
              width: "auto",
              maxWidth: "none",
            },
            spotlight: {
              borderradius: "8px",
              border: "2px solid #7285b3",
              boxshadow: "0 0 15px rgba(114, 133, 179, 0.5)",
            },
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
            arrow: {
              color: "#ffffff",
            },
          }}
        />
      )}
    </>
  );
};

export default TourGuide;
